import loadable from '@loadable/component'
import { matchRoutes, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useLayoutEffect, useMemo } from 'react'

import AuthRouteElement from 'components/App/Routes/AuthRouteElement'
import Trip404Page from 'tripPlanner/pages/Trip404Page'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useAppDispatch } from 'hooks/reduxHooks'
import { registerPageView } from 'actions/AnalyticsActions'

const MapView = loadable(
  () =>
    import(
      'tripPlanner/pages/WebViews/components/MapView/MapView'
    ),
)

const TourAndCruiseItemSummaryView = loadable(
  () =>
    import(
      'tripPlanner/pages/WebViews/components/TourAndCruiseItemSummaryView/TourAndCruiseItemSummaryView'
    ),
)

const ExperienceItemSummaryView = loadable(
  () =>
    import(
      'tripPlanner/pages/WebViews/components/ExperienceItemSummaryView/ExperienceItemSummaryView'
    ),
)

const SavedLandingView = loadable(
  () =>
    import(
      'tripPlanner/pages/WebViews/components/SavedViewWebView/SavedViewWebView'
    ),
)

const webViewRoutePaths = [
  '/:regionCode/trip-planner/webView/:tripId/search-experience',
  '/:regionCode/trip-planner/webView/:tripId/share-trip',
  '/:regionCode/trip-planner/webView/:tripId/search-flights',
  '/:regionCode/trip-planner/webView/:tripId/search-tours-or-cruises',
  '/:regionCode/trip-planner/webView/:tripId/search-le-booking',
  '/:regionCode/trip-planner/webView/:tripId/add-to-trip',
  '/:regionCode/trip-planner/webView/:tripId/map',
  '/:regionCode/trip-planner/webView/:tripId/custom-flight',
  '/:regionCode/trip-planner/webView/:tripId/edit-custom-flight/:itemId',
  '/:regionCode/trip-planner/webView/:tripId/tour-cruise-summary/:itemId',
  '/:regionCode/trip-planner/webView/:tripId/experience-summary/:itemId',
  '/:regionCode/trip-planner/webView/saved',
]

const webViewRoutes = webViewRoutePaths.map(path => ({ path }))

function WebViewRoutes() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => {
    return {
      ...location,
      pathname: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location])
  const matchedRoute = useMemo(() => {
    return matchRoutes(webViewRoutes, localeStrippedLocation)?.at(-1)
  }, [localeStrippedLocation])

  useLayoutEffect(() => {
    if (!matchedRoute) return

    dispatch(registerPageView({
      template: matchedRoute.route.path,
      params: matchedRoute.params,
      path: matchedRoute.pathname,
      search: location.search,
    }))
  }, [dispatch, location.search, matchedRoute])

  return (
    <Routes location={localeStrippedLocation}>
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/search-experience"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/search-experience"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/share-trip"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/share-trip"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/search-flights"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/search-flights"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/search-tours-or-cruises"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/search-tours-or-cruises"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/search-le-booking"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/search-le-booking"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/add-to-trip"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/add-to-trip"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/map"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/map"><MapViewRoute /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/custom-flight"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/custom-flight"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/edit-custom-flight/:itemId"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/edit-custom-flight/:itemId"><Trip404Page /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/tour-cruise-summary/:itemId"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/tour-cruise-summary/:itemId"><TourAndCruiseItemSummaryRoute /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/:tripId/experience-summary/:itemId"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/:tripId/experience-summary/:itemId"><ExperienceItemSummaryRoute /></WebViewAuthRoute>}
      />
      <Route
        path="/:regionCode/trip-planner/webView/saved"
        element={<WebViewAuthRoute path="/:regionCode/trip-planner/webView/saved"><SavedLandingView /></WebViewAuthRoute>}
      />
      <Route path="*" element={<Trip404Page />} />
    </Routes>
  )
}

interface WebViewAuthRouteProps {
  children: React.ReactNode
  path: string
}

function WebViewAuthRoute(props: WebViewAuthRouteProps) {
  return (
    <AuthRouteElement path={props.path}>
      {props.children}
    </AuthRouteElement>
  )
}

function MapViewRoute() {
  const match = useWebViewRouteMatch<{ tripId: string }>()
  return <MapView match={match} />
}

function TourAndCruiseItemSummaryRoute() {
  const match = useWebViewRouteMatch<{ tripId: string, itemId: string }>()
  return <TourAndCruiseItemSummaryView match={match} />
}

function ExperienceItemSummaryRoute() {
  const match = useWebViewRouteMatch<{ tripId: string, itemId: string }>()
  return <ExperienceItemSummaryView match={match} />
}

function useWebViewRouteMatch<Params extends Record<string, string>>() {
  const params = useParams()
  return useMemo(() => ({
    params: params as Params,
  }), [params])
}

export default WebViewRoutes
